import React from 'react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, Cell 
} from 'recharts';
import { Lock, BarChart2, Star } from 'lucide-react';
import { useTranslation } from '../../../../contexts/LanguageContext';

const MobileMerchantStatsTab = ({ merchant, isPremiumUser }) => {
  const { t } = useTranslation();

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const getChartData = () => {
    if (!merchant) return [];
    return merchant.subscriptionPlans.map(plan => ({
      name: plan.name,
      users: plan.userCount,
      percentage: ((plan.userCount / merchant.stats.subscribers) * 100).toFixed(1),
      color: plan.color
    }));
  };

  if (!isPremiumUser) {
    return (
      <div className="bg-white rounded-xl p-4 border border-gray-100 text-center">
        <div className="bg-yellow-100 p-3 rounded-full w-max mx-auto">
          <Lock className="h-6 w-6 text-yellow-600" />
        </div>
        <h3 className="mt-3 text-lg font-medium">
          {t('merchant.statsTab.premiumFeature')}
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          {t('merchant.statsTab.upgradeForStats')}
        </p>
        <button
          disabled
          className="mt-4 bg-gray-300 text-gray-500 px-4 py-2 rounded-lg text-sm cursor-not-allowed"
        >
          {t('merchant.statsTab.upgradeToPremium')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Plan Distribution Chart - Now on top */}
      <div className="bg-white p-4 rounded-xl border border-gray-100">
        <div className="flex items-center mb-3">
          <BarChart2 className="h-5 w-5 text-[#253985] mr-2" />
          <h3 className="text-md font-semibold text-[#253985]">
            {t('merchant.statsTab.planDistribution')}
          </h3>
        </div>
        
        {merchant.stats.subscribers >= 50 ? (
          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={getChartData()}
                layout="vertical"
                margin={{ top: 10, right: 10, left: 40, bottom: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10 }} />
                <YAxis 
                  dataKey="name" 
                  type="category" 
                  width={80}
                  tick={{ fontSize: 10 }}
                />
                <Tooltip
                  formatter={(value, name) => {
                    if (name === 'users') {
                      return [`${formatNumber(value)} ${t('merchant.statsTab.subscribers').toLowerCase()}`, t('merchant.statsTab.subscribers')];
                    }
                    return [value, name];
                  }}
                  labelFormatter={(label) => `${label} ${t('merchant.common.plan')}`}
                  contentStyle={{ fontSize: 12 }}
                />
                <Bar dataKey="users" name={t('merchant.statsTab.subscribers')} radius={[0, 4, 4, 0]}>
                  {getChartData().map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-64 w-full flex items-center justify-center bg-gray-50 rounded-lg border border-dashed border-gray-300">
            <p className="text-gray-500 text-sm">
              {t('merchant.statsTab.noRepresentativeData') || "No representative data"}
            </p>
          </div>
        )}
      </div>

      {/* Key Metrics (without total subscribers) */}
      <h2 className="text-lg font-semibold text-[#253985]">
        {t('merchant.statsTab.keyMetrics')}
      </h2>
      
      <div className="space-y-3">
        {/* Average Rating */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg border border-blue-100">
          <h3 className="text-sm font-medium text-gray-500 mb-1">
            {t('merchant.statsTab.avgRating')}
          </h3>
          <div className="flex items-center">
            <div className="text-xl font-bold text-[#253985] mr-2">
              {merchant.stats.avgRating}
            </div>
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className={`h-4 w-4 ${i < Math.floor(merchant.stats.avgRating) ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`}
                />
              ))}
            </div>
          </div>
        </div>
        
        {/* Satisfaction Rate */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg border border-blue-100">
          <h3 className="text-sm font-medium text-gray-500 mb-1">
            {t('merchant.statsTab.satisfactionRate')}
          </h3>
          <div className="flex items-center">
            <div className="text-xl font-bold text-[#253985] mr-2">
              {merchant.stats.satisfactionRate}%
            </div>
            <div className="flex-1 bg-gray-200 rounded-full h-2">
              <div 
                className="bg-green-500 h-2 rounded-full" 
                style={{ width: `${merchant.stats.satisfactionRate}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* Additional Stats */}
      <div className="space-y-3 mt-4">
        {/* Popular Plan */}
        <div className="bg-white p-4 rounded-xl border border-gray-100">
          <h3 className="text-md font-semibold text-[#253985] mb-2">
            {t('merchant.statsTab.popularPlan')}
          </h3>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-lg font-bold text-[#253985]">
                {merchant.subscriptionPlans[0]?.name || t('merchant.statsTab.standard')}
              </p>
              <p className="text-xs text-gray-500">
                {t('merchant.statsTab.mostSubscribed')}
              </p>
            </div>
            <div className="bg-blue-100 p-2 rounded-full">
              <Star className="h-5 w-5 text-[#253985]" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileMerchantStatsTab;